import { proposalQueryKeys } from "@platform/mpx-quotes-grid";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";

import type { Proposal, ProposalScope } from "@platform/mpx-quotes-grid";
import type { AxiosInstance } from "axios";

/** Request body for PATCH /btx/v1/proposal-plan-groups/:id */
interface UpdateProposalPlanGroupFootnoteRequest {
  footnote: string;
}

const patchProposalPlanGroupFootnote = async (
  axiosInstance: AxiosInstance,
  groupId: string,
  footnote: string,
): Promise<void> => {
  const body: UpdateProposalPlanGroupFootnoteRequest = { footnote };
  await axiosInstance.patch(`btx/v1/proposal-plan-groups/${groupId}`, body);
};

interface UseProposalPlanGroupFootnoteMutationOptions {
  rfpId: string;
  axiosInstance: AxiosInstance;
  /** The proposals read scope (ADR-031) currently rendered in the grid — the
   * optimistic cache write must target this exact scoped query, not the
   * unscoped `.all` key, since `setQueryData`/`getQueryData` require an exact
   * key match (unlike `invalidateQueries`, which prefix-matches every scope). */
  scope: ProposalScope;
  onSaveError?: (err: Error) => void;
}

/**
 * Saves a carrier group's footnote (the row at the bottom of the rates table).
 * A footnote is one-per-carrier, so it PATCHes the plan group — not a plan like
 * rates do. Mirrors the optimistic-update pattern of the rates benefits mutation.
 */
export const useProposalPlanGroupFootnoteMutation = ({
  rfpId,
  axiosInstance,
  scope,
  onSaveError,
}: UseProposalPlanGroupFootnoteMutationOptions): {
  saveFootnote: (groupId: string, footnote: string) => void;
} => {
  const queryClient = useQueryClient();
  // `.all` matches every scoped variant for cancel/invalidate (React Query
  // prefix-matches queryKey arrays); the optimistic read/write below needs
  // the exact scoped key instead, since it renders the grid's live data.
  const queryKey = proposalQueryKeys.all(rfpId);
  const scopedQueryKey = proposalQueryKeys.scoped(rfpId, scope);

  const { mutate } = useMutation({
    mutationFn: ({
      groupId,
      footnote,
    }: {
      groupId: string;
      footnote: string;
    }) => patchProposalPlanGroupFootnote(axiosInstance, groupId, footnote),
    onMutate: async ({
      groupId,
      footnote,
    }: {
      groupId: string;
      footnote: string;
    }) => {
      await queryClient.cancelQueries({ queryKey });

      const previousProposals =
        queryClient.getQueryData<Proposal[]>(scopedQueryKey);

      queryClient.setQueryData<Proposal[]>(scopedQueryKey, (old) => {
        if (!old) return old;
        return old.map((proposal) => ({
          ...proposal,
          coverages: proposal.coverages.map((coverage) => ({
            ...coverage,
            planGroups: coverage.planGroups.map((group) =>
              group.id === groupId ? { ...group, footnote } : group,
            ),
          })),
        }));
      });

      // Captured here (not read from the outer closure in onError) so
      // rollback targets the scope that was actually patched — `scope` (and
      // thus `scopedQueryKey`) can change while this mutation is still in
      // flight (e.g. toggling quote history), which would otherwise roll
      // back into the wrong cache entry.
      return { previousProposals, scopedQueryKey };
    },
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey, refetchType: "none" }),
    onError: (err: Error, _vars, context) => {
      if (context?.previousProposals !== undefined) {
        queryClient.setQueryData(
          context.scopedQueryKey,
          context.previousProposals,
        );
      }
      onSaveError?.(err);
    },
  });

  const saveFootnote = useCallback(
    (groupId: string, footnote: string) => mutate({ groupId, footnote }),
    [mutate],
  );

  return { saveFootnote };
};
